// Maze.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faLightbulb, faCheckCircle, faQuestionCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

export default function Maze({ gridSize = 6, youtubeLink }) {
  const [showHelp, setShowHelp] = useState(false);
  const [notification, setNotification] = useState(null);
  const [grid, setGrid] = useState([]);
  const [playerPos, setPlayerPos] = useState([0, 0]);
  const [completed, setCompleted] = useState(false);

  // Generate simple maze grid: 0 = open, 1 = wall
  const generateMaze = (size) => {
    return Array(size)
      .fill(null)
      .map(() =>
        Array(size)
          .fill(null)
          .map(() => (Math.random() > 0.7 ? 1 : 0))
      );
  };

  useEffect(() => {
    const newMaze = generateMaze(gridSize);
    newMaze[0][0] = 0; // Start
    newMaze[gridSize - 1][gridSize - 1] = 0; // End
    setGrid(newMaze);
    setPlayerPos([0, 0]);
  }, [gridSize]);

  const movePlayer = (dr, dc) => {
    const [r, c] = playerPos;
    const newR = r + dr;
    const newC = c + dc;

    if (
      newR >= 0 &&
      newR < gridSize &&
      newC >= 0 &&
      newC < gridSize &&
      grid[newR][newC] === 0
    ) {
      setPlayerPos([newR, newC]);

      if (newR === gridSize - 1 && newC === gridSize - 1) {
        setCompleted(true);
        setNotification({ type: 'success', message: '🎉 You reached the end!' });
        setTimeout(() => setNotification(null), 3000);
      }
    } else {
      setNotification({ type: 'error', message: '🚫 Cannot move there!' });
      setTimeout(() => setNotification(null), 2000);
    }
  };

  const resetMaze = () => {
    const newMaze = generateMaze(gridSize);
    newMaze[0][0] = 0;
    newMaze[gridSize - 1][gridSize - 1] = 0;
    setGrid(newMaze);
    setPlayerPos([0, 0]);
    setCompleted(false);
  };

  return (
    <div className="p-2 min-h-screen bg-black text-gray-300 flex flex-col items-center">
      {/* Top Buttons */}
      <div className="w-full flex justify-center gap-4 items-center mb-4">
        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex justify-center items-center gap-2"
          onClick={() => window.history.back()}
        >
          ← Back
        </button>

        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-blue-600 text-black rounded-lg hover:bg-blue-500 flex justify-center items-center gap-2"
          onClick={() => setShowHelp(true)}
        >
          <FontAwesomeIcon icon={faQuestionCircle} /> Help
        </button>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-900 p-6 rounded-xl max-w-lg w-full text-gray-300 flex flex-col gap-4">
            <h3 className="text-m lg:text-xl font-bold text-green-400">📖 How to Play Maze</h3>
            <p className='text-m lg:text-lg'>
              Navigate the maze from the top-left corner (start) to the bottom-right corner (end).
            </p>
            <ul className="list-disc list-inside text-gray-400">
              <li>Use the arrow buttons or tap adjacent cells to move.</li>
              <li>You cannot move through walls (gray blocks).</li>
              <li>Reach the bottom-right cell to complete the maze.</li>
              <li>Reset button will generate a new maze.</li>
            </ul>
            {youtubeLink && (
              <a
                href={youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-red-600 text-black rounded-lg hover:bg-red-500 text-center font-semibold"
              >
                ▶ Watch
              </a>
            )}
            <button
              className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 mt-2"
              onClick={() => setShowHelp(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg text-sm font-semibold ${
            notification.type === 'error' ? 'bg-red-700 text-white' : 'bg-green-600 text-black'
          } flex justify-between items-center shadow-lg z-50 w-80`}
        >
          <span>{notification.message}</span>
          <button
            className="w-10 p-1 text-lg flex items-center justify-center"
            onClick={() => setNotification(null)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Title */}
      {completed && (
        <div className="text-green-400 text-xl flex items-center gap-2 mb-2 animate-pulse">
          <FontAwesomeIcon icon={faCheckCircle} /> Maze Completed!
        </div>
      )}
      <h2 className="text-lg lg:text-2xl font-bold mb-2 text-green-400">🌀 Maze Challenge</h2>

      {/* Maze Grid */}
      <div className="flex flex-col items-center">
        {grid.map((row, rIdx) => (
          <div key={rIdx} className="flex">
            {row.map((cell, cIdx) => {
              const isPlayer = playerPos[0] === rIdx && playerPos[1] === cIdx;
              return (
                <div
                  key={cIdx}
                  onClick={() => movePlayer(rIdx - playerPos[0], cIdx - playerPos[1])}
                  className={`w-12 h-12 border m-0.5 flex items-center justify-center cursor-pointer transition 
                    ${isPlayer ? 'bg-green-400' : cell === 1 ? 'bg-gray-700' : 'bg-gray-900 hover:bg-gray-800'}`}
                >
                  {isPlayer && <span className="text-black font-bold">🚶</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="mt-4 flex flex-row gap-4 flex-wrap justify-center">
        <button
          onClick={() => movePlayer(-1, 0)}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-600 transition"
        >
          ↑ Up
        </button>
        <button
          onClick={() => movePlayer(1, 0)}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-600 transition"
        >
          ↓ Down
        </button>
        <button
          onClick={() => movePlayer(0, -1)}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-600 transition"
        >
          ← Left
        </button>
        <button
          onClick={() => movePlayer(0, 1)}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-600 transition"
        >
          → Right
        </button>
        <button
          onClick={resetMaze}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-blue-600 text-black font-semibold rounded-lg hover:bg-blue-500 transition"
        >
          🔄 Reset
        </button>
      </div>
    </div>
  );
}
